import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      setStatusMessage('Thank you for subscribing!');
      setEmail('');
      return;
    }
    const formData = new FormData(form);
    try {
      const response = await fetch('https://readdy.ai/api/form/d97s2hnk7gok24d49tf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      const code = parsed?.code as string | undefined;
      if (response.ok && code === 'OK') {
        setStatus('success');
        setStatusMessage('Thanks for subscribing! Stay tuned for updates.');
        setEmail('');
      } else {
        const serverMsg = (parsed?.meta as Record<string, unknown>)?.message as string
          || parsed?.message as string
          || responseText;
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setStatus('error');
          setStatusMessage('Something went wrong. Please try again.');
        } else {
          setStatus('error');
          setStatusMessage(typeof serverMsg === 'string' ? serverMsg : 'Something went wrong. Please try again.');
        }
      }
    } catch {
      setStatus('error');
      setStatusMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="bg-accent-900 text-background-50/80">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-14 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          <div>
            <h4 className="font-heading text-xl font-bold text-background-50 mb-4">
              HipoBuy
            </h4>
            <p className="text-sm leading-relaxed text-background-50/60">
              Your ultimate resource for Hipobuy Spreadsheet product research. Discover, compare, and shop smarter with organized product categories and guides.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-background-50/50 mb-4">
              Subscribe
            </h4>
            <form
              onSubmit={handleSubscribe}
              data-readdy-form
              className="space-y-3"
            >
              <div className="relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="w-full bg-transparent border-0 border-b border-background-50/20 text-background-50 placeholder:text-background-50/30 py-2 pr-10 text-sm focus:outline-none focus:border-background-50/50 transition-colors"
                />
                <button
                  type="submit"
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-background-50/60 hover:text-background-50 transition-colors"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
              <div className="honeypot-field">
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              {status !== 'idle' && (
                <p className={`text-xs ${status === 'success' ? 'text-accent-300' : 'text-red-400'}`}>
                  {statusMessage}
                </p>
              )}
              <p className="text-xs text-background-50/40">
                No spam, unsubscribe anytime.
              </p>
            </form>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-background-50/50 mb-4">
              Resources
            </h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/hipobuy-spreadsheet-guide" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  Complete Guide
                </Link>
              </li>
              <li>
                <Link to="/hipobuy-spreadsheet-review" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  Full Review
                </Link>
              </li>
              <li>
                <Link to="/hipobuy-vs-kakobuy-spreadsheet" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  Kakobuy Comparison
                </Link>
              </li>
              <li>
                <Link to="/hipobuy-spreadsheet-safety" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  Safety Guide
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-background-50/50 mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  Hipobuy Sheet
                </a>
              </li>
              <li>
                <Link to="/" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/hipobuy-spreadsheet-beginners" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  Beginner Tutorial
                </Link>
              </li>
              <li>
                <Link to="/hipobuy-spreadsheet-tips" className="text-sm text-background-50/70 hover:text-background-50 transition-colors">
                  Tips & Tricks
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-background-50/10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
          <div className="text-center">
            <div className="font-heading text-5xl md:text-7xl lg:text-9xl font-bold text-background-50/5 tracking-tighter select-none leading-none">
              HIPOBUY
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between mt-8 pt-6 border-t border-background-50/10 text-xs text-background-50/40">
            <p>&copy; 2026 Hipobuy Spreadsheet. All rights reserved.</p>
            <div className="flex items-center space-x-4 mt-2 sm:mt-0">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-background-50/70 transition-colors">
                Privacy
              </a>
              <span className="text-background-50/20">|</span>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-background-50/70 transition-colors">
                Disclaimer
              </a>
              <span className="text-background-50/20">|</span>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="hover:text-background-50/70 transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}